import DeleteIcon from "@mui/icons-material/Delete"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import WarningIcon from "@mui/icons-material/Warning"
import { useTheme, CircularProgress, Typography, Stack } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import IconButton from "@mui/material/IconButton"
import { useConfirm } from "material-ui-confirm"
import { ReactElement, useEffect, useState } from "react"
import { KeyedMutator } from "swr"

import MfaChip from "./mfaChip"
import {
    TT_DELETE_USER_BUTTON,
    TT_FALSE_MFA,
    TT_DELETE_OWN_ACCOUNT,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useDeleteQuicksightUser from "../../../services/middleware/hooks/adminPortal/useDeleteQuicksightUser"
import useUpdateQuicksight from "../../../services/middleware/hooks/adminPortal/useUpdateQuicksight"
import {
    TUserOutput,
    TUsersOutput,
} from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import CustomRadioButton from "../../buttons/customRadioButton"
import AppTooltip from "../../helper/appTooltip"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IUserRowProps {
    user: TUserOutput
    mutate: KeyedMutator<TUsersOutput>
}

/**
 * Displays each user in a single row within a table
 * It shows user details such as name, email, MFA status, and QuickSight access roles.
 */
const SysAdminUsersRows = ({ user, mutate }: IUserRowProps): ReactElement => {
    const theme = useTheme()
    const confirm = useConfirm()
    const { updateQuicksight, isLoading: updateQuicksightLoading } =
        useUpdateQuicksight(user.email, user.fullName, mutate)

    const { deleteQuicksightUser, isLoading: deleteUserLoading } =
        useDeleteQuicksightUser(user.email, user.fullName, mutate)

    const { isEnaSysAdmin, email: loggedInUserEmail } = useGetUserData()
    const isFalseMfa = user.mfa === false
    const tableCellSx = { opacity: isFalseMfa ? 0.5 : 1 }
    const [selected, setSelected] = useState("")

    // Set the selected role based on the user's QuickSight access
    useEffect(() => {
        if (user.isInsightsAuthor) {
            setSelected("Author")
        } else if (user.isInsightsReader) {
            setSelected("Reader")
        } else {
            setSelected("None")
        }
    }, [user, setSelected])

    // Display a confirmation dialog before deleting a user
    const deleteConformation = (): void => {
        confirm({
            title: (
                <Typography variant="h6" component="div" align="center">
                    Are you sure?
                </Typography>
            ),
            description: (
                <Typography variant="body1" component="span">
                    <WarningIcon
                        sx={{
                            fontSize: "2.5rem",
                            color: theme.palette.warning.main,
                            verticalAlign: "middle",
                            marginRight: "1rem",
                        }}
                    />
                    {user.fullName} will be permanently deleted and removed
                </Typography>
            ),
        })
            .then(() => {
                deleteQuicksightUser()
            })
            .catch(() => {
                /* ... */
            })
    }

    const isRowLoading = (): boolean =>
        updateQuicksightLoading || deleteUserLoading

    const isRowDisabled = (): boolean => isRowLoading() || isFalseMfa

    const isDeleteDisabled = (): boolean =>
        isRowLoading() || user.email === loggedInUserEmail

    // Update the user's QuickSight access role
    const handleButtonClick = async (userRole: string): Promise<void> => {
        const previousSelection = selected
        setSelected(userRole)

        await updateQuicksight(userRole.toUpperCase(), () => {
            setSelected(previousSelection)
        })
    }

    return (
        <AppTooltip title={user.mfa === true ? "" : TT_FALSE_MFA}>
            <AppTableContentRow>
                <AppTableCell sx={tableCellSx}>
                    {isRowLoading() ? (
                        <CircularProgress />
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                            }}
                        >
                            {user.initials}
                        </Avatar>
                    )}
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Stack direction="row">
                        <Typography color="text.primary" marginRight={1}>
                            {user.fullName}
                        </Typography>
                        <DisplayIf condition={isEnaSysAdmin}>
                            <AppTooltip title="This user is an admin">
                                <ShieldOutlinedIcon
                                    sx={{ color: "text.primary" }}
                                />
                            </AppTooltip>
                        </DisplayIf>
                    </Stack>
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    {" "}
                    <Typography color="text.primary">{user.email}</Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx} align="center">
                    <MfaChip isFalseMfa={isFalseMfa} />
                </AppTableCell>
                <AppTableCell align="center">
                    <span
                        style={{
                            display: "flex",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            justifyContent: "center",
                        }}
                    >
                        <CustomRadioButton
                            label="Author"
                            selected={selected}
                            handleClick={() => handleButtonClick("Author")}
                            disabled={isRowDisabled()}
                        />
                        <CustomRadioButton
                            label="Reader"
                            selected={selected}
                            handleClick={() => handleButtonClick("Reader")}
                            disabled={isRowDisabled()}
                        />
                        <CustomRadioButton
                            label="None"
                            selected={selected}
                            handleClick={() => handleButtonClick("None")}
                            disabled={isRowDisabled()}
                        />
                    </span>
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip
                        title={
                            isDeleteDisabled()
                                ? TT_DELETE_OWN_ACCOUNT
                                : TT_DELETE_USER_BUTTON
                        }
                    >
                        <span>
                            <IconButton
                                color="error"
                                onClick={deleteConformation}
                                disabled={isDeleteDisabled()}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </AppTooltip>
                </AppTableCell>
            </AppTableContentRow>
        </AppTooltip>
    )
}

export default SysAdminUsersRows
